"use client";

import { Button } from "@pex/ui-web";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useRef, useState } from "react";

// Browsers only allow autoplay while muted, so the clip starts silent and
// loops. The control overlay fades in on hover/focus (and stays visible on
// touch devices, which can't hover) to expose play/pause and unmute. State is
// driven off the video's own events so the icons stay in sync regardless of
// how playback or volume changes. Scoped to this component — it only affects
// the SGF testimonial video.
export function HopeFamilyTestimonialVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      video.play().catch(() => {
        // Ignore — nothing actionable if the browser declines playback.
      });
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = !video.muted;
  };

  return (
    <div className="group relative aspect-square overflow-hidden rounded-2xl">
      <video
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
        loop
        muted
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
        playsInline
        ref={videoRef}
      >
        <source src="/videos/hope-family-testimonial.mp4" type="video/mp4" />
      </video>

      {/* Controls — revealed on hover/focus; always shown on touch devices.
          Built on the cta-secondary (glass) variant for system radius, focus
          ring, blur and press motion, but the border is overridden to stay
          transparent in every state so only the hover blur reads as glass. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-end gap-2 p-4 opacity-0 transition-opacity duration-200 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
        <Button
          aria-label={isPlaying ? "Pause video" : "Play video"}
          className="pointer-events-auto border-transparent hover:border-transparent active:border-transparent"
          onClick={togglePlay}
          size="icon"
          type="button"
          variant="cta-secondary"
        >
          {isPlaying ? (
            <Pause aria-hidden="true" />
          ) : (
            <Play aria-hidden="true" />
          )}
        </Button>
        <Button
          aria-label={isMuted ? "Unmute video" : "Mute video"}
          className="pointer-events-auto border-transparent hover:border-transparent active:border-transparent"
          onClick={toggleMute}
          size="icon"
          type="button"
          variant="cta-secondary"
        >
          {isMuted ? (
            <VolumeX aria-hidden="true" />
          ) : (
            <Volume2 aria-hidden="true" />
          )}
        </Button>
      </div>
    </div>
  );
}
